import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Convert base64 string to Uint8Array (needed for VAPID key)
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Register service worker - defined outside hook to avoid declaration order issues
const registerServiceWorker = async () => {
  try {
    const reg = await navigator.serviceWorker.register('/sw-push.js', {
      scope: '/'
    });
    
    return reg;
  } catch (err) {
    console.error('Service Worker registration failed:', err);
    return null;
  }
};

export const usePushNotifications = () => {
  // Check if push notifications are supported synchronously
  const supported = typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window;
  const initialPermission = supported ? Notification.permission : 'default';
  
  const [isSupported] = useState(supported);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [permission, setPermission] = useState(initialPermission);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [registration, setRegistration] = useState(null);

  // Register service worker and check subscription on mount
  useEffect(() => {
    if (!isSupported) return;
    
    // Register and check subscription
    registerServiceWorker().then(async (reg) => {
      if (reg) {
        setRegistration(reg);
        const subscription = await reg.pushManager.getSubscription();
        setIsSubscribed(!!subscription);
      }
    });
  }, [isSupported]);

  // Subscribe to push notifications
  const subscribe = useCallback(async () => {
    if (!isSupported) {
      setError('Push notifications are not supported in this browser');
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      // Request notification permission
      const permissionResult = await Notification.requestPermission();
      setPermission(permissionResult);
      
      if (permissionResult !== 'granted') {
        setError('Notification permission denied');
        setLoading(false);
        return false;
      }

      // Get or register service worker
      let reg = registration;
      if (!reg) {
        reg = await registerServiceWorker();
        if (!reg) {
          setError('Failed to register service worker');
          setLoading(false);
          return false;
        }
      }

      // Wait for service worker to be ready
      await navigator.serviceWorker.ready;

      // Get VAPID public key from server
      const token = localStorage.getItem('token');
      const keyResponse = await axios.get(`${API}/api/push/vapid-public-key`);
      const vapidPublicKey = keyResponse.data.public_key;

      if (!vapidPublicKey) {
        setError('Server push configuration not available');
        setLoading(false);
        return false;
      }

      // Subscribe to push notifications
      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });

      // Send subscription to server
      const subscriptionData = subscription.toJSON();
      await axios.post(
        `${API}/api/push/subscribe`,
        {
          endpoint: subscriptionData.endpoint,
          keys: subscriptionData.keys
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setIsSubscribed(true);
      setLoading(false);
      return true;

    } catch (err) {
      console.error('Push subscription error:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to subscribe to push notifications');
      setLoading(false);
      return false;
    }
  }, [isSupported, registration]);

  // Unsubscribe from push notifications
  const unsubscribe = useCallback(async () => {
    if (!registration) return false;

    setLoading(true);
    setError(null);

    try {
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        // Unsubscribe from browser
        await subscription.unsubscribe();

        // Remove from server
        const token = localStorage.getItem('token');
        await axios.delete(`${API}/api/push/unsubscribe`, {
          params: { endpoint: subscription.endpoint },
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      setIsSubscribed(false);
      setLoading(false);
      return true;

    } catch (err) {
      console.error('Push unsubscribe error:', err);
      setError(err.message || 'Failed to unsubscribe from push notifications');
      setLoading(false);
      return false;
    }
  }, [registration]);

  // Check subscription status
  const checkSubscription = useCallback(async () => {
    if (!registration) return false;
    
    try {
      const subscription = await registration.pushManager.getSubscription();
      setIsSubscribed(!!subscription);
      return !!subscription;
    } catch {
      return false;
    }
  }, [registration]);

  return {
    isSupported,
    isSubscribed,
    permission,
    loading,
    error,
    subscribe,
    unsubscribe,
    checkSubscription
  };
};

export default usePushNotifications;
